import {
  dismissLeagueCard,
  leagueCardAtom,
} from "@app/league/atoms/league-card.atom";
import { useLeagueFormat } from "@app/league/hooks/use-league-format.hook";
import { leagueCardLine } from "@app/league/utils/merge-league-cards";
import type { LeagueCard } from "@core/league/types";
import { URLS } from "@core/shared/utils";
import { cn } from "@design-system/utils";
import { trans, plainTrans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { ArrowDown, ArrowUp, Trophy, X } from "lucide-react";
import { useEffect } from "react";
import { LeagueAvatar } from "./LeagueAvatar";

/**
 * Content of the after-activity card: «+25 League points · you passed Sara ·
 * #7 → #6». Used by the floating host and inline on the round result.
 *
 * @example
 * <LeagueCardContent card={card} />
 */
export function LeagueCardContent({ card }: { card: LeagueCard }) {
  const { num } = useLeagueFormat();
  const line = leagueCardLine(card);
  const moved =
    card.rankBefore !== null &&
    card.rankAfter !== null &&
    card.rankBefore !== card.rankAfter;
  const up =
    moved && (card.rankAfter as number) < (card.rankBefore as number);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
        >
          <Trophy className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-black text-slate-950 dark:text-white">
            {trans("league.card.points", { points: num(card.pointsDelta) })}
          </p>
          {card.source !== "unknown" ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {trans(`league.card.from.${card.source}`)}
            </p>
          ) : null}
        </div>
        {card.rankAfter !== null ? (
          <p
            className={cn(
              "flex shrink-0 items-center gap-1 text-lg font-black tabular-nums",
              up ? "text-emerald-600 dark:text-emerald-400" : "text-slate-700 dark:text-slate-200",
            )}
          >
            {moved ? (
              <>
                <s className="text-sm font-bold opacity-60">#{num(card.rankBefore as number)}</s>
                {up ? (
                  <ArrowUp aria-label={plainTrans("league.card.rankUpLabel")} className="h-4 w-4" role="img" />
                ) : (
                  <ArrowDown aria-label={plainTrans("league.card.rankDownLabel")} className="h-4 w-4" role="img" />
                )}
              </>
            ) : null}
            #{num(card.rankAfter)}
          </p>
        ) : null}
      </div>
      {card.passed.length > 0 ? (
        <div className="flex items-center gap-2 text-sm font-black text-slate-900 dark:text-white">
          <LeagueAvatar className="h-8 w-8" student={card.passed[0]} />
          <span className="min-w-0 truncate">
            {trans(line.key, { name: line.params.name, rank: String(line.params.rank) })}
          </span>
        </div>
      ) : (
        <p className="text-sm font-bold text-slate-600 dark:text-slate-300">
          {trans(line.key, { name: line.params.name, rank: String(line.params.rank) })}
        </p>
      )}
    </div>
  );
}

/**
 * Floating after-activity card, mounted once in the root layout. It listens
 * to `leagueCardAtom` (set when a reward response carries a `leagueCard`)
 * and hides itself after 8 seconds; the timer is an effect with cleanup.
 *
 * @example
 * <LeaguePointsCardHost />
 */
export function LeaguePointsCardHost() {
  const notice = leagueCardAtom.useValue();

  useEffect(() => {
    if (!notice) return;

    const timer = window.setTimeout(dismissLeagueCard, 8000);

    return () => window.clearTimeout(timer);
  }, [notice]);

  if (!notice) return null;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4"
      role="status"
    >
      <div className="pointer-events-auto relative w-full max-w-sm rounded-3xl border-2 border-b-4 border-primary-200 bg-white p-4 shadow-xl dark:border-primary-800 dark:bg-slate-900">
        <button
          aria-label={plainTrans("league.card.dismiss")}
          className="absolute end-2 top-2 rounded-full p-1 text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-slate-200"
          onClick={dismissLeagueCard}
          type="button"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
        <LeagueCardContent card={notice.card} />
        <Link
          className="mt-3 block text-center text-xs font-black text-primary-700 hover:underline dark:text-primary-300"
          onClick={dismissLeagueCard}
          to={`${URLS.league}?tab=week`}
        >
          {trans("league.card.open")}
        </Link>
      </div>
    </div>
  );
}
